import React, { useEffect, useState } from 'react';
import { Slider, InputNumber, message } from 'antd';
import practiceClient from '../../client/PracticeClient';
import useTranslation from '../../hooks/useTranslation';
import './LearningSettingsPanel.css';

/**
 * Настройки обучения: сколько помнить и сколько нового в день.
 *
 * Одна ручка на виду — желаемая удержка. Так же устроены Anki и SuperMemo, и по той же
 * причине: остальные два десятка чисел модели памяти подбираются по ответам, а не
 * человеком. Выше удержка — короче интервалы и больше повторений; это единственный
 * компромисс, который стоит доверить пользователю.
 *
 * Настройки общие для всех колод и форматов, потому что след памяти у слова один.
 */
const RETENTION_MARKS = { 70: '70%', 80: '80%', 90: '90%', 99: '99%' };

const LearningSettingsPanel = () => {
    const { t } = useTranslation();
    const [settings, setSettings] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        practiceClient.getLearningSettings()
            .then((loaded) => {
                if (!cancelled) {
                    setSettings(loaded);
                }
            })
            .catch((error) => {
                console.error('Не удалось загрузить настройки обучения:', error);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const save = async (patch) => {
        const next = { ...settings, ...patch };
        setSettings(next);
        setIsSaving(true);
        try {
            setSettings(await practiceClient.updateLearningSettings(patch));
        } catch (error) {
            console.error('Не удалось сохранить настройки обучения:', error);
            message.error(t('practice.settingsApplyFailed'));
        } finally {
            setIsSaving(false);
        }
    };

    if (!settings) {
        return null;
    }

    return (
        <div className="learningSettings">
            <div className="learningSettings__row">
                <div className="learningSettings__label">
                    {t('practice.desiredRetention')}
                    <span className="learningSettings__hint">{t('practice.desiredRetentionHint')}</span>
                </div>
                <Slider
                    className="learningSettings__slider"
                    min={70}
                    max={99}
                    marks={RETENTION_MARKS}
                    disabled={isSaving}
                    value={Math.round(settings.desiredRetention * 100)}
                    onChangeComplete={(value) => save({ desiredRetention: value / 100 })}
                />
            </div>

            <div className="learningSettings__row learningSettings__row--compact">
                <div className="learningSettings__label">{t('practice.newPerDay')}</div>
                <InputNumber
                    min={1}
                    max={1000}
                    disabled={isSaving}
                    value={settings.newPerDay}
                    onChange={(value) => value && save({ newPerDay: value })}
                />
            </div>
        </div>
    );
};

export default LearningSettingsPanel;
